<template>
  <main ref="scroller" class="soil-page" @touchstart.passive="touchStart" @touchmove="touchMove" @touchend="touchEnd"
    @touchcancel="touchCancel" @click.capture="guardGestureClick">
    <div ref="pullContent" class="page-content">
      <div ref="pullFeedback" class="pull-feedback" role="status" aria-hidden="true">下拉刷新</div>
      <div v-if="!deviceName" class="empty">请从 App 设备列表打开墒情站</div>
      <template v-else>
        <section class="card overview" v-loading="busy.realtime">
          <div class="device-heading">
            <img class="device-image" src="@/assets/equipment/soil-station.png" alt="墒情站设备" />
            <div class="device-identity">
              <h2>{{ deviceName }}</h2>
              <p class="device-name">{{ deviceTitle }}</p>
              <p v-if="location" class="device-location">{{ location }}</p>
            </div>
            <div class="device-telemetry">
              <div class="telemetry-item"><svg viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M2 14h3v4H2zm4-4h3v8H6zm4-4h3v12h-3zm4-4h3v16h-3z" fill="currentColor" />
                </svg><span><small>信号</small><b>{{ display(report.signal) }}</b></span></div>
              <div class="telemetry-item"><svg viewBox="0 0 24 20" aria-hidden="true">
                  <rect x="1" y="4" width="19" height="12" rx="2" fill="none" stroke="currentColor"
                    stroke-width="1.6" />
                  <path d="M22 8v4M11 6L7 11h4l-1 3 5-5h-4z" fill="currentColor" stroke="currentColor" />
                </svg><span><small>电压</small><b>{{ number(report.voltage) === null ? '—' : number(report.voltage) / 1000
                    + ' V' }}</b></span></div>
            </div>
          </div>
          <p v-if="errors.realtime" class="error" role="alert">{{ errors.realtime }} <button class="retry"
              @click="loadRealtime">重试</button></p>
        </section>
        <section v-if="layers.length || number(report.t0) !== null || number(report.ph) !== null" class="layers card"
          aria-label="实时墒情">
          <div class="section-head soil-heading">
            <h3>土层实时数据</h3>
            <div class="collected-time"><span><i class="el-icon-time" aria-hidden="true" />采集时间 {{
              report.deviceTime || report.collectTime || '暂无数据' }}</span></div>
          </div>
          <div v-if="displayDepths.length" class="comparison-hint"><span
              class="temperature-legend">温度条：−20～60℃</span><span class="moisture-legend">水分条：0～100%</span></div>
          <div v-if="number(report.ph) !== null" class="surface-metrics">
            <span v-if="number(report.ph) !== null">酸碱度 <b class="ph">{{ report.ph }} pH</b></span>
          </div>
          <table v-if="displayDepths.length" class="soil-table" aria-label="各土层温度、水分和EC对比">
            <thead>
              <tr>
                <th scope="col" class="depth-cell">深度</th>
                <th scope="col" class="temperature-heading">温度 ℃</th>
                <th scope="col" class="moisture-heading">水分 %</th>
                <th v-if="hasRealtimeEC" scope="col" class="ec-heading">EC<small>mS/cm</small></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="depth in displayDepths" :key="depth">
                <th scope="row" class="depth-cell"><strong>{{ depth === 0 ? '地表' : depthLabel(depth) }}</strong><span
                    v-if="depth !== 0" class="status" :class="statusClass(depth)">{{ statusText(depth) }}</span></th>
                <td><strong class="temperature">{{ display(report['t' + depth]) }}</strong>
                  <div v-if="number(report['t' + depth]) !== null" class="temperature-track" aria-hidden="true"><span
                      :style="{ width: Math.max(0, Math.min(100, (number(report['t' + depth]) + 20) / 80 * 100)) + '%' }" />
                  </div>
                </td>
                <td><template v-if="depth !== 0"><strong class="moisture">{{ display(report['mc' + depth]) }}</strong>
                    <div v-if="number(report['mc' + depth]) !== null" class="moisture-track" aria-hidden="true"><span
                        :style="{ width: Math.max(0, Math.min(100, number(report['mc' + depth]))) + '%' }" /></div>
                  </template>
                </td>
                <td v-if="hasRealtimeEC"><strong v-if="number(report['ec' + depth]) !== null" class="ec">{{ report['ec'
                    + depth]
                    }}</strong><span v-else class="missing-value" aria-label="无EC数据"></span></td>
              </tr>
            </tbody>
          </table>
        </section>
        <p v-if="!busy.realtime && !errors.realtime && !layers.length && number(report.t0) === null && number(report.ph) === null"
          class="empty">暂无实时墒情数据</p>
        <section class="card details">
          <div class="tabs" role="tablist"><button v-for="tab in tabs" :key="tab.key" role="tab"
              :aria-selected="activeTab === tab.key" :class="{ active: activeTab === tab.key }"
              @click="selectTab(tab.key)">{{
              tab.label }}</button></div>
          <div class="tab-panels" :style="{ minHeight: tabMinHeight + 'px' }">
            <div v-show="activeTab === 'history'" ref="historyPanel" class="panel" v-loading="busy.history">
              <div class="history-filter">
                <div class="filter-heading"><i class="el-icon-date" />查询时间<span>最多查询31天</span></div>
                <div class="quick-ranges"><el-button @click="setRange(7)">近一周</el-button><el-button
                    @click="setRange(29)">近一个月</el-button></div>
                <div class="date-range">
                  <label>开始日期<el-date-picker v-model="start" type="date" placeholder="选择开始日期" format="yyyy年MM月dd日"
                      value-format="yyyy-MM-dd" :editable="false" :clearable="false" :picker-options="startDateOptions"
                      popper-class="soil-date-picker" /></label>
                  <label>结束日期<el-date-picker v-model="end" type="date" placeholder="选择结束日期" format="yyyy年MM月dd日"
                      value-format="yyyy-MM-dd" :editable="false" :clearable="false" :picker-options="endDateOptions"
                      popper-class="soil-date-picker" /></label>
                </div>
                <el-button class="query" type="primary" :disabled="busy.history" @click="loadHistory"
                  icon="el-icon-search">查询历史记录</el-button>
              </div>
              <div class="metric-tabs"><button v-for="metric in historyMetrics" :key="metric.key"
                  :class="{ active: selectedMetric === metric.key }" @click="selectedMetric = metric.key">{{
                  metric.label
                  }}</button></div>
              <div class="chart-area">
                <div v-show="chartSeries.length && !errors.history" ref="chart" class="chart" role="img"
                  :aria-label="currentMetric.label + '历史曲线，可点击图例切换土层，拖动底部滑块缩放'" />
                <p v-if="errors.history" class="chart-state error" role="alert">{{ errors.history }}</p>
                <p v-else-if="!busy.history && !chartSeries.length" class="chart-state empty">该时间段暂无{{
                  currentMetric.label }}数据
                </p>
              </div>
            </div>
            <div v-show="activeTab === 'threshold'" class="panel" :style="{ minHeight: tabMinHeight + 'px' }"
              v-loading="busy.threshold">
              <div class="section-head"><span class="hint">土壤水分阈值（%）</span><el-button :loading="initializing"
                  :disabled="saving !== null || busy.threshold" @click="initialize">初始化阈值</el-button></div>
              <p v-if="errors.threshold" class="error" role="alert">{{ errors.threshold }} <button class="retry"
                  @click="loadThresholds">重试</button></p>
              <p v-else-if="!busy.threshold && !visibleThresholds.length" class="empty">暂无阈值，可先初始化</p>
              <article v-for="row in visibleThresholds" :key="row.id" class="threshold-row">
                <h3>{{ depthLabel(row.depth) }}</h3>
                <div class="threshold-fields"><label>下限（%）<el-input v-model="row.lowerLimit" inputmode="decimal"
                      :disabled="saving !== null || initializing" /></label><label>上限（%）<el-input
                      v-model="row.upperLimit" inputmode="decimal"
                      :disabled="saving !== null || initializing" /></label></div>
                <p class="hint">设定时间：{{ row.updateDate || '—' }}<br />操作人：{{ row.creater || '—' }}</p>
                <el-button type="primary" :loading="saving === row.id" :disabled="saving !== null || initializing"
                  @click="saveThreshold(row)">保存阈值</el-button>
              </article>
            </div>
            <div v-show="activeTab === 'alarm'" class="panel" :style="{ minHeight: tabMinHeight + 'px' }"
              v-loading="busy.alarm">
              <div class="section-head"><span class="hint">共 {{ alarms.length }} 条预警</span><el-button
                  :disabled="busy.alarm" @click="loadAlarms">刷新</el-button></div>
              <p v-if="errors.alarm" class="error" role="alert">{{ errors.alarm }}</p>
              <p v-else-if="!busy.alarm && !alarms.length" class="empty">暂无阈值预警</p>
              <article v-for="(alarm, index) in pageAlarms" :key="alarm.id || index" class="alarm-row">
                <p class="alarm-message">{{ alarmMessage(alarm.alarmMessage) }}</p>
                <p>{{ alarm.alarmTime || '—' }}</p>
                <p>{{ alarm.deviceName || deviceName }}</p>
              </article>
              <nav v-if="alarms.length > 10" class="alarm-pagination" aria-label="预警分页">
                <button aria-label="上一页" :disabled="page <= 1 || busy.alarm" @click="page--"><i
                    class="el-icon-arrow-left" />上一页</button>
                <span class="page-indicator" aria-live="polite">第 <b>{{ page }}</b> / {{ Math.ceil(alarms.length / 10)
                  }}
                  页</span>
                <button aria-label="下一页" :disabled="page >= Math.ceil(alarms.length / 10) || busy.alarm"
                  @click="page++">下一页<i class="el-icon-arrow-right" /></button>
              </nav>
            </div>
          </div>
        </section>
      </template>
    </div>
  </main>
</template>

<script>
import * as echarts from 'echarts/core'
import { LineChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent, DataZoomComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import request from '@/utils/request'

echarts.use([LineChart, GridComponent, TooltipComponent, LegendComponent, DataZoomComponent, CanvasRenderer])
const depths = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100]
const depthMap = { 70: 80, 80: 100, 90: 120, 100: 140 }
const statuses = ['墒情适宜', '极旱', '特旱', '重度干旱', '中度干旱', '轻微干旱', '轻微渍水', '中度渍水', '重度渍涝', '积水淹涝']
const dateText = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

export default {
  data() {
    return {
      pullRefreshing: false, suppressClickUntil: 0, tabMinHeight: 0,
      report: {}, location: '', history: [], thresholds: [], alarms: [], page: 1,
      activeTab: 'history', selectedMetric: 'temperature', start: '', end: '', today: dateText(new Date()),
      busy: { realtime: false, history: false, threshold: false, alarm: false },
      errors: { realtime: '', history: '', threshold: '', alarm: '' },
      loaded: { threshold: false, alarm: false }, initializing: false, saving: null,
      tabs: [{ key: 'history', label: '历史记录' }, { key: 'threshold', label: '阈值设定' }, { key: 'alarm', label: '阈值预警' }],
      metrics: [{ key: 'temperature', prefix: 't', label: '温度', unit: '℃' }, { key: 'moisture', prefix: 'mc', label: '水分', unit: '%' }, { key: 'ec', prefix: 'ec', label: 'EC', unit: 'mS/cm' }, { key: 'ph', prefix: 'ph', label: 'pH', unit: '' }],
    }
  },
  computed: {
    startDateOptions() { return { firstDayOfWeek: 1, disabledDate: date => dateText(date) > (this.end || this.today) || dateText(date) > this.today } },
    endDateOptions() { return { firstDayOfWeek: 1, disabledDate: date => dateText(date) < this.start || dateText(date) > this.today } },
    historyMetrics() { return this.metrics.filter(metric => metric.key !== 'ec' || this.history.some(row => depths.some(depth => this.number(row['ec' + depth]) !== null))) },
    deviceName() { return String(this.$route.query.deviceName || '') },
    deviceTitle() { return [this.$route.query.landName, this.$route.query.displayName].filter(value => value && value !== 'null').join(' · ') || '墒情站' },
    is14m() { return String(this.$route.query.displayName || '').includes('1.4m') },
    layers() { return depths.filter(depth => ['t', 'mc', 'ec'].some(prefix => this.number(this.report[prefix + depth]) !== null)) },
    displayDepths() { return this.number(this.report.t0) === null ? this.layers : [0, ...this.layers] },
    hasRealtimeEC() { return depths.some(depth => this.number(this.report['ec' + depth]) !== null) },
    visibleThresholds() { const max = Math.max(0, ...this.layers); return this.thresholds.filter(row => !max || parseInt(row.depth, 10) <= max) },
    pageAlarms() { return this.alarms.slice((this.page - 1) * 10, this.page * 10) },
    currentMetric() { return this.metrics.find(metric => metric.key === this.selectedMetric) },
    chartSeries() {
      const metric = this.currentMetric
      const fields = metric.key === 'ph' ? [{ key: 'ph', label: 'pH' }] : (metric.key === 'temperature' ? [0, ...depths] : depths).map(depth => ({ key: metric.prefix + depth, label: depth === 0 ? '地表' : this.depthLabel(depth) }))
      return fields.map(field => ({ name: field.label, type: 'line', showSymbol: true, symbolSize: 4, connectNulls: false, data: this.history.map(row => this.number(row[field.key])) })).filter(series => series.data.some(value => value !== null))
    },
  },
  watch: {
    selectedMetric() { this.renderChart() },
    historyMetrics(metrics) { if (!metrics.some(metric => metric.key === this.selectedMetric)) this.selectedMetric = 'temperature' },
  },
  mounted() {
    this.chartInstance = null
    window.addEventListener('resize', this.resizeChart)
    this.setRange(7, false)
    if (this.deviceName) { this.loadRealtime(); this.loadHistory(); this.loadLocation() }
  },
  beforeDestroy() {
    this.disposed = true
    clearTimeout(this._pullSettleTimer)
    cancelAnimationFrame(this._pullFrame)
    window.removeEventListener('resize', this.resizeChart)
    if (this.chartInstance) this.chartInstance.dispose()
  },
  methods: {
    touchStart(event) {
      this.touchCancel()
      this.suppressClickUntil = 0
      if (event.touches.length !== 1 || this.pullRefreshing || !this.deviceName || this.initializing || this.saving !== null || event.target.closest('input, button, .chart')) return
      const point = event.touches[0]
      if (point.clientX <= 28) return
      this._touchGesture = { x: point.clientX, y: point.clientY, direction: '', atTop: this.$refs.scroller.scrollTop <= 0 }
    },
    touchMove(event) {
      const gesture = this._touchGesture
      if (!gesture || event.touches.length !== 1) { this.touchCancel(); return }
      const point = event.touches[0], dx = point.clientX - gesture.x, dy = point.clientY - gesture.y
      if (Math.max(Math.abs(dx), Math.abs(dy)) < 8) return
      if (!gesture.direction) gesture.direction = dy > 0 && Math.abs(dy) > Math.abs(dx) && gesture.atTop && !Object.values(this.busy).some(Boolean) ? 'pull' : 'scroll'
      if (gesture.direction !== 'pull') return
      if (event.cancelable) event.preventDefault()
      const distance = Math.max(0, dy * 0.5)
      this.setPullDistance(distance <= 56 ? distance : 56 + 40 * (1 - Math.exp(-(distance - 56) / 40)), true)
    },
    touchEnd() {
      const gesture = this._touchGesture, refresh = gesture && gesture.direction === 'pull' && this._pullDistance >= 56
      if (gesture && gesture.direction === 'pull') this.suppressClickUntil = Date.now() + 500
      this.touchCancel()
      if (refresh) this.refreshFromPull()
    },
    touchCancel() { this._touchGesture = null; if (!this.pullRefreshing) this.setPullDistance(0) },
    setPullDistance(distance, dragging = false) {
      // Gesture coordinates stay outside Vue reactivity: do not rerender every device per touch.
      this._pullDistance = distance
      this._pullDragging = dragging
      if (this._pullFrame) return
      this._pullFrame = requestAnimationFrame(() => {
        this._pullFrame = null
        const content = this.$refs.pullContent, feedback = this.$refs.pullFeedback
        if (!content || !feedback) return
        content.style.transition = this._pullDragging ? 'none' : 'transform 260ms cubic-bezier(.22,.61,.36,1)'
        clearTimeout(this._pullSettleTimer)
        this._pullSettling = !this._pullDragging
        content.style.willChange = 'transform'
        if (!this._pullDragging) this._pullSettleTimer = setTimeout(() => { this._pullSettling = false; content.style.willChange = '' }, 280)
        content.style.transform = this._pullDistance ? 'translate3d(0,' + this._pullDistance + 'px,0)' : ''
        feedback.setAttribute('aria-hidden', this._pullDistance ? 'false' : 'true')
        const message = this.pullRefreshing ? '正在刷新' : this._pullDistance >= 56 ? '松开刷新' : '下拉刷新'
        if (feedback.textContent !== message) feedback.textContent = message
      })
    },
    async refreshFromPull() {
      if (Object.values(this.busy).some(Boolean) || this.saving !== null || this.initializing || this.pullRefreshing || !this.deviceName) return
      this.pullRefreshing = true
      this.setPullDistance(40)
      this.loaded.threshold = false
      this.loaded.alarm = false
      const reload = this.activeTab === 'threshold' ? this.loadThresholds : this.activeTab === 'alarm' ? this.loadAlarms : this.loadHistory
      try { await Promise.all([this.loadRealtime(), reload(), new Promise(resolve => setTimeout(resolve, 280))]) }
      finally { this.pullRefreshing = false; if (!this.disposed) this.setPullDistance(0) }
    },
    guardGestureClick(event) { if (Date.now() < this.suppressClickUntil) { event.preventDefault(); event.stopPropagation() } },

    notify(type, message) { this.$message({ type, message, customClass: 'soil-message' }) },
    number(value) { if (value === null || value === undefined || String(value).trim() === '') return null; const result = Number(value); return Number.isFinite(result) ? result : null },
    display(value) { return this.number(value) === null ? '—' : value },
    depthLabel(depth) { const number = parseInt(depth, 10); return `${this.is14m && depthMap[number] ? depthMap[number] : number}cm` },
    statusCode(depth) { return (String(this.report.mcStatus == null ? '' : this.report.mcStatus).match(/[-0-9]/g) || [])[depths.indexOf(depth)] },
    statusText(depth) { return statuses[this.statusCode(depth)] || '暂无分析' },
    statusClass(depth) { const code = this.statusCode(depth); return code === '0' ? 'suitable' : '12345'.includes(code) ? 'dry' : '6789'.includes(code) ? 'wet' : '' },
    alarmMessage(value) { return this.is14m ? String(value || '').replace(/(^|[^\d])(70|80|90|100)(\s*cm)/gi, (_, prefix, depth, unit) => prefix + depthMap[depth] + unit) : value || '—' },
    async api(path, params = {}) {
      const response = await request({ url: '/api/' + path, method: 'get', params: { deviceName: this.deviceName, ...params } })
      if (typeof response === 'string' && /<(!doctype|html)/i.test(response)) throw new Error('请返回 App 重新登录后重试')
      return response
    },
    list(response) { if (Array.isArray(response)) return response; if (response && Array.isArray(response.report)) return response.report; if (response && Array.isArray(response.data)) return response.data; throw new Error('返回数据格式异常，请重新登录或稍后重试') },
    errorText(error, fallback) { return error.response && [401, 403].includes(error.response.status) ? '请返回 App 重新登录后重试' : error.message && error.message.includes('请') ? error.message : fallback },
    async loadRealtime() {
      if (this.busy.realtime) return
      this.busy.realtime = true; this.errors.realtime = ''
      try {
        const result = await this.api('map/getSoilMoisture')
        if (result && (typeof result !== 'object' || Array.isArray(result) || result.success === false)) throw new Error('返回数据格式异常，请重新登录或稍后重试')
        this.report = result || {}
      } catch (error) { this.errors.realtime = this.errorText(error, '实时数据获取失败，请重试') }
      finally { this.busy.realtime = false }
    },
    async loadLocation() {
      const { longitude, latitude, farmId } = this.$route.query
      if (String(farmId) === '31156') { this.location = '新疆维吾尔自治区-塔城地区-塔城市'; return }
      if (this.number(longitude) === null || this.number(latitude) === null) return
      try {
        let result = await this.api('map/getLocationByCoord', { lng: longitude, lat: latitude })
        if (typeof result === 'string') result = JSON.parse(result)
        const address = result && result.result && result.result.addressComponent
        if (address) this.location = [address.province, address.city, address.district].filter(Boolean).join('-')
      } catch (_) { /* 地址解析失败不影响设备数据展示。 */ }
    },
    setRange(days, load = true) { const end = new Date(); const start = new Date(); start.setDate(start.getDate() - days); this.start = dateText(start); this.end = dateText(end); if (load) this.loadHistory() },
    async loadHistory() {
      if (this.busy.history) return
      const duration = new Date(this.end).getTime() - new Date(this.start).getTime()
      if (!this.start || !this.end || !Number.isFinite(duration) || duration < 0 || duration > 31 * 86400000 || this.end > this.today) { this.notify('error', '请选择不超过31天且不晚于今天的日期范围'); return }
      this.busy.history = true; this.errors.history = ''
      try { this.history = this.list(await this.api('map/getSoilMoistureHistory', { start: this.start, end: this.end })) }
      catch (error) { this.errors.history = this.errorText(error, '历史记录获取失败，请重试') }
      finally { this.busy.history = false; this.renderChart() }
    },
    async renderChart() {
      await this.$nextTick()
      if (this.disposed || this.activeTab !== 'history' || !this.$refs.chart || !this.chartSeries.length || this.errors.history) return
      if (!this.chartInstance) this.chartInstance = echarts.init(this.$refs.chart)
      this.chartInstance.setOption({
        animation: false,
        color: ['#5BA1E6', '#35A56F', '#E9A23B', '#9B72CF', '#E46F76', '#37A8AC', '#7789CB', '#9AAB49', '#C77B44', '#5F7B99', '#B56AAB'],
        tooltip: { trigger: 'axis', confine: true, renderMode: 'richText', valueFormatter: value => value === null ? '—' : value + ' ' + this.currentMetric.unit },
        legend: { type: 'scroll', top: 8, left: 0, right: 0, textStyle: { color: '#61788F' } },
        grid: { left: 10, right: 16, top: 65, bottom: 65, containLabel: true },
        xAxis: { type: 'category', boundaryGap: false, data: this.history.map(row => row.collectTime || row.deviceTime || ''), axisLabel: { hideOverlap: true, formatter: value => String(value).slice(5, 16).replace(' ', '\n') } },
        yAxis: { type: 'value', name: this.currentMetric.unit || 'pH', scale: true },
        dataZoom: [{ type: 'slider', bottom: 6, height: 24 }],
        series: this.chartSeries,
      }, true)
      this.resizeChart()
    },
    resizeChart() { if (this.chartInstance) this.chartInstance.resize() },
    selectTab(key) {
      // Keep the first lazy-loaded panels from shrinking the page and clamping scrollTop.
      const historyPanel = this.$refs.historyPanel
      if (historyPanel && historyPanel.offsetHeight) this.tabMinHeight = historyPanel.offsetHeight
      this.activeTab = key; if (key === 'history') this.renderChart(); if (key === 'threshold' && !this.loaded.threshold) this.loadThresholds(); if (key === 'alarm' && !this.loaded.alarm) this.loadAlarms()
    },
    async loadThresholds() {
      if (this.busy.threshold) return
      this.busy.threshold = true; this.errors.threshold = ''
      try { this.thresholds = this.list(await this.api('threshold/getThresholdLists')); this.loaded.threshold = true }
      catch (error) { this.loaded.threshold = false; this.errors.threshold = this.errorText(error, '阈值获取失败，请重试') }
      finally { this.busy.threshold = false }
    },
    async initialize() {
      if (this.initializing || this.saving !== null) return
      try { await this.$confirm('初始化可能重置已有阈值，是否继续？', '初始化阈值', { confirmButtonText: '初始化', cancelButtonText: '取消', type: 'warning', customClass: 'soil-confirm' }) } catch (_) { return }
      this.initializing = true
      try {
        const result = await this.api('threshold/initThreshold')
        if ((result && result.success === false) || (typeof result === 'string' && /失败|错误|无权|未登录/.test(result))) throw new Error('初始化失败，请稍后重试')
        this.notify('success', '阈值初始化成功'); await this.loadThresholds()
      } catch (error) { this.notify('error', this.errorText(error, '初始化失败，请重试')) }
      finally { this.initializing = false }
    },
    async saveThreshold(row) {
      if (this.saving !== null || this.initializing) return
      const lower = this.number(row.lowerLimit); const upper = this.number(row.upperLimit)
      if (lower === null || upper === null || lower < 0 || upper > 100 || lower >= upper) { this.notify('error', '水分阈值需满足：0 ≤ 下限 < 上限 ≤ 100'); return }
      this.saving = row.id
      try {
        const result = await this.api('threshold/updateThreshold', { id: row.id, upperLimit: upper, lowerLimit: lower })
        if (result !== '更新成功') { this.notify('error', typeof result === 'string' ? result : '阈值保存失败'); return }
        this.notify('success', '阈值保存成功'); await this.loadThresholds(); this.loadRealtime()
      } catch (error) { this.notify('error', this.errorText(error, '阈值保存失败，请重试')) }
      finally { this.saving = null }
    },
    async loadAlarms() {
      if (this.busy.alarm) return
      this.busy.alarm = true; this.errors.alarm = ''
      try { this.alarms = this.list(await this.api('alarm/getAlarmLogs')); this.page = 1; this.loaded.alarm = true }
      catch (error) { this.loaded.alarm = false; this.errors.alarm = this.errorText(error, '预警记录获取失败，请重试') }
      finally { this.busy.alarm = false }
    },
  },
}
</script>

<style scoped>
.soil-page {
  box-sizing: border-box;
  height: 100vh;
  height: 100dvh;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior-y: none;
  -webkit-overflow-scrolling: touch;
  padding: 12px 12px calc(24px + env(safe-area-inset-bottom, 0px));
  background: #edf6ff;
  color: #26384a;
  text-align: left;
}

.page-content {
  position: relative;
  width: 100%;
}

.pull-feedback[aria-hidden="true"] {
  visibility: hidden;
}

.pull-feedback {
  position: absolute;
  top: -40px;
  left: 0;
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  overflow: hidden;
  color: #808080;
  font-size: 12px;
}

.chart,
.chart ::v-deep canvas {
  touch-action: pan-y;
}

.card {
  background: #fff;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 12px;
}

h2,
h3,
p {
  margin: 0;
}

h2 {
  font-size: 17px;
  overflow-wrap: anywhere;
}

h3 {
  font-size: 15px;
}

p {
  font-size: 12px;
  line-height: 1.7;
  color: #8096ad;
  margin-top: 6px;
  overflow-wrap: anywhere;
}

.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.section-head>div {
  min-width: 0;
}

.section-head .el-button {
  flex-shrink: 0;
}

.device-heading {
  display: flex;
  align-items: center;
  gap: 12px;
}

.device-identity {
  flex: 1;
  min-width: 0;
}

.device-identity h2 {
  font-size: 17px;
  line-height: 1.35;
  font-variant-numeric: tabular-nums;
}

.device-name {
  font-size: 12px;
  color: #61788f;
  line-height: 1.5;
  margin-top: 5px;
}

.device-location {
  font-size: 11px;
  margin-top: 3px;
}

.device-image {
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  border-radius: 50%;
  object-fit: contain;
}

.device-telemetry {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;
  border-left: 1px solid #e4f0fb;
  padding-left: 10px;
}

.telemetry-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.telemetry-item svg {
  width: 18px;
  height: 18px;
  color: #35a56f;
  flex-shrink: 0;
}

.telemetry-item small {
  display: block;
  color: #8096ad;
  font-size: 10px;
  line-height: 1.2;
}

.telemetry-item b {
  display: block;
  margin-top: 2px;
  color: #26384a;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.soil-heading {
  align-items: center;
}

.soil-heading h3 {
  flex-shrink: 0;
}

.collected-time {
  text-align: right;
  color: #8096ad;
  font-size: 12px;
  line-height: 1.5;
}

.collected-time span {
  display: block;
}

.collected-time i {
  margin-right: 4px;
}

.collected-time time {
  display: block;
  color: #61788f;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.surface-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 20px;
  margin-top: 12px;
  padding: 10px;
  background: #f2f7fc;
  border-radius: 8px;
  color: #8096ad;
  font-size: 12px;
}

.surface-metrics b {
  margin-left: 4px;
  font-variant-numeric: tabular-nums;
}

.soil-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  margin-top: 14px;
  font-variant-numeric: tabular-nums;
}

.soil-table th,
.soil-table td {
  text-align: center;
  padding: 13px 4px;
  border-bottom: 1px solid #e7f0f8;
}

.soil-table thead th {
  padding: 11px 4px;
  background: #f2f7fc;
  color: #496985;
  font-size: 13px;
  font-weight: 600;
  border-bottom: 2px solid #dce9f5;
}

.soil-table thead th:first-child {
  border-radius: 8px 0 0 0;
}

.soil-table thead th:last-child {
  border-radius: 0 8px 0 0;
}

.soil-table thead .temperature-heading {
  background: #fff7e9;
  color: #b77a1c;
  border-bottom-color: #edc782;
}

.soil-table thead .moisture-heading {
  background: #eaf4ff;
  color: #347fc5;
  border-bottom-color: #98c6ee;
}

.soil-table thead .ec-heading {
  background: #edf8f2;
  color: #2c9663;
  border-bottom-color: #a1d5b9;
}

.soil-table thead small {
  color: inherit;
  opacity: .75;
  font-weight: normal;
}

.soil-table small {
  display: block;
  margin-top: 3px;
  font-size: 10px;
  color: #8096ad;
}

.soil-table .depth-cell {
  width: 72px;
  text-align: left;
  padding-left: 8px;
}

.soil-table tbody th strong {
  display: block;
  font-size: 14px;
  font-weight: 600;
}

.soil-table .status {
  display: block;
  margin-top: 5px;
  font-size: 10px;
  font-weight: normal;
}

.soil-table td {
  vertical-align: top;
  padding-top: 16px;
}

.soil-table td strong {
  display: block;
  font-size: 21px;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.soil-table tbody tr:last-child>* {
  border-bottom: 0;
}

.temperature-track,
.moisture-track {
  height: 4px;
  max-width: 64px;
  margin: 7px auto 0;
  background: #e4f0fb;
  border-radius: 3px;
  overflow: hidden;
}

.temperature-track span,
.moisture-track span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: #5ba1e6;
}

.temperature-track {
  background: #fbefd9;
}

.temperature-track span {
  background: #e5a23e;
}

.comparison-hint {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  margin-top: 12px;
  color: #8096ad;
  font-size: 10px;
}

.comparison-hint span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.comparison-hint span::before {
  content: "";
  width: 12px;
  height: 4px;
  border-radius: 2px;
  background: #e5a23e;
}

.comparison-hint .moisture-legend::before {
  background: #5ba1e6;
}

.temperature {
  color: #cc871e;
}

.moisture {
  color: #358ed5;
}

.ec {
  color: #2c9663;
}

.ph {
  color: #9b72cf;
}

.alarm-pagination {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-top: 16px;
}

.alarm-pagination button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  min-height: 48px;
  padding: 10px 8px;
  border: 1px solid #cfe2f5;
  border-radius: 9px;
  color: #347fc5;
  background: #edf6ff;
  font-size: 14px;
  cursor: pointer;
}

.alarm-pagination button:disabled {
  background: #f5f7fa;
  border-color: #e7edf3;
  color: #a3afbb;
  cursor: default;
}

.page-indicator {
  flex-shrink: 0;
  color: #8096ad;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.page-indicator b {
  color: #26384a;
  font-size: 15px;
}

.status {
  font-size: 12px;
  color: #8096ad;
}

.suitable {
  color: #27965d;
}

.dry,
.error {
  color: #d84f50;
}

.wet {
  color: #168bd2;
}

.empty {
  text-align: center;
  padding: 24px 8px;
  color: #8096ad;
  font-size: 13px;
}

.details {
  padding: 0;
  overflow: hidden;
  overflow-anchor: none;
}

.tabs {
  display: flex;
  border-bottom: 1px solid #e4f0fb;
}

.tabs button,
.metric-tabs button {
  flex: 1;
  border: 0;
  background: transparent;
  color: #8096ad;
  min-height: 44px;
  font-size: 14px;
  cursor: pointer;
}

.tabs button.active {
  color: #3989cd;
  box-shadow: inset 0 -3px #5ba1e6;
  font-weight: bold;
}

.panel {
  box-sizing: border-box;
  padding: 12px;
  min-height: 100px;
}

.quick-ranges {
  display: flex;
  gap: 8px;
}

.date-range,
.threshold-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin: 12px 0;
}

label {
  font-size: 12px;
  color: #61788f;
  min-width: 0;
}

.query {
  width: 100%;
}

.metric-tabs {
  display: flex;
  background: #f2f7fc;
  border-radius: 8px;
  margin-top: 16px;
}

.metric-tabs button.active {
  color: #3989cd;
  background: #e0effd;
  border-radius: 8px;
}

.chart-area {
  position: relative;
  height: 374px;
}

.chart {
  width: 100%;
  height: 340px;
}

.chart-state {
  position: absolute;
  inset: 0 0 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 16px;
  text-align: center;
}

.chart-hint {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 28px;
  margin: 0;
  font-size: 11px;
}

.hint {
  font-size: 12px;
  color: #8096ad;
}

.threshold-row,
.alarm-row {
  padding: 16px 0;
  border-bottom: 1px solid #e4f0fb;
}

.threshold-row:last-child,
.alarm-row:last-child {
  border-bottom: 0;
}

.threshold-row .el-button {
  margin-top: 10px;
  width: 100%;
}

.alarm-message {
  color: #bd6940;
  font-size: 14px;
}

.retry {
  border: 0;
  background: transparent;
  color: #3989cd;
  min-height: 40px;
}

.history-filter {
  padding: 12px;
  border: 1px solid #e4f0fb;
  border-radius: 10px;
  background: #f5f9fd;
}

.filter-heading {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
  font-size: 13px;
  font-weight: 600;
}

.filter-heading i {
  color: #5ba1e6;
}

.filter-heading span {
  margin-left: auto;
  font-size: 11px;
  font-weight: normal;
  color: #8096ad;
}

.quick-ranges ::v-deep .el-button {
  flex: 1;
  margin: 0;
  border-color: #dce9f5;
  border-radius: 20px;
  color: #3989cd;
  background: white;
}

.date-range ::v-deep .el-date-editor {
  width: 100%;
}

.date-range ::v-deep .el-input__inner {
  padding-left: 27px;
  padding-right: 4px;
  font-size: 12px;
  height: 42px;
  border-radius: 7px;
  background: white;
}

.date-range ::v-deep .el-input__prefix {
  left: 2px;
}

::v-deep .el-button {
  min-height: 40px;
  padding: 10px 12px;
  border-radius: 7px;
}

::v-deep .el-button--primary {
  background: #5ba1e6;
  border-color: #5ba1e6;
}

::v-deep .el-input {
  margin-top: 6px;
}

::v-deep .el-input__inner {
  border-color: #dce8f2;
}

::v-deep .el-loading-mask {
  border-radius: 12px;
}

@media (max-width: 360px) {
  .device-heading {
    gap: 8px;
  }

  .device-image {
    width: 48px;
    height: 48px;
  }

  .device-identity h2 {
    font-size: 15px;
  }

  .device-telemetry {
    padding-left: 8px;
  }

  .soil-table .depth-cell {
    width: 64px;
    padding-left: 4px;
  }

  .soil-table td strong {
    font-size: 18px;
  }

  .alarm-pagination {
    gap: 6px;
  }

  .date-range {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (min-width: 650px) {
  .soil-page {
    max-width: 1000px;
    margin: auto;
  }

  .chart-area {
    height: 434px;
  }

  .chart {
    height: 400px;
  }
}
</style>

<style>
.soil-date-picker {
  width: 292px;
  max-width: calc(100vw - 16px);
  color: #26384a;
  border-color: #e4f0fb;
  border-radius: 12px;
}

.soil-date-picker .el-picker-panel__content {
  width: auto;
  margin: 10px;
}

.soil-date-picker .el-date-table td {
  height: 40px;
}

.soil-date-picker .el-date-table td.current:not(.disabled) span {
  background: #5ba1e6;
}

.soil-date-picker .el-date-table td.today span {
  color: #3989cd;
}

.soil-message {
  min-width: 0;
  width: calc(100% - 24px);
  max-width: 420px;
}

.soil-message .el-message__content {
  overflow-wrap: anywhere;
}

.soil-confirm {
  width: calc(100% - 32px);
  max-width: 420px;
}

.soil-confirm .el-button {
  min-height: 40px;
}

.soil-confirm .el-button--primary {
  background: #5ba1e6;
  border-color: #5ba1e6;
}
</style>
